import { createFileRoute } from '@tanstack/react-router'
import React, { useEffect, useState, useCallback } from 'react';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useI18n } from '@/hooks/useI18n';

interface TimeOffRequest {
    id: string;
    organization_id: string;
    employee_id: string;
    employee_first_name?: string;
    employee_last_name?: string;
    policy_type_ref_id: string;
    policy_type_name: string | null;
    start_timestamp: number;
    end_timestamp: number;
    timezone: string;
    is_all_day: boolean;
    status: 'pending' | 'approved' | 'denied';
    employee_note: string | null;
    manager_note: string | null;
    created_at: string;
    updated_at: string | null;
}

interface TimeOffRequestsResponse {
    requests: TimeOffRequest[];
    total: number;
    page: number;
    per_page: number;
}

interface PolicyType {
    id: string;
    name: string;
}

interface Employee {
    id: string;
    first_name: string;
    last_name: string;
}

export default function TimeOffRequestsPage() {
    const { t } = useI18n();
    const [requests, setRequests] = useState<TimeOffRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [statusFilter, setStatusFilter] = useState<string>('pending');
    const [policyTypeFilter, setPolicyTypeFilter] = useState<string>('');
    const [employeeFilter, setEmployeeFilter] = useState<string>('');

    // Reference data
    const [policyTypes, setPolicyTypes] = useState<PolicyType[]>([]);
    const [employees, setEmployees] = useState<Employee[]>([]);

    // Review modal state
    const [reviewingRequest, setReviewingRequest] = useState<TimeOffRequest | null>(null);
    const [reviewAction, setReviewAction] = useState<'approve' | 'deny' | null>(null);
    const [managerNote, setManagerNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Load reference data
    useEffect(() => {
        const loadReferenceData = async () => {
            try {
                const [typesResp, employeesResp] = await Promise.all([
                    apiFetch('/api/timesheet/time-off-policies/types'),
                    apiFetch('/api/people/employees/?limit=200&is_active=true')
                ]);

                if (typesResp.ok) {
                    const typesData = await typesResp.json();
                    setPolicyTypes(typesData || []);
                }

                if (employeesResp.ok) {
                    const employeesData = await employeesResp.json();
                    setEmployees(employeesData?.employees || []);
                }
            } catch (err) {
                console.error('Failed to load reference data:', err);
            }
        };

        loadReferenceData();
    }, []);

    // Load requests
    const loadRequests = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams();
            params.set('per_page', '100');

            if (statusFilter) {
                params.set('status', statusFilter);
            }
            if (policyTypeFilter) {
                params.set('policy_type_ref_id', policyTypeFilter);
            }
            if (employeeFilter) {
                params.set('employee_id', employeeFilter);
            }

            const response = await apiFetch(`/api/timesheet/time-off-requests?${params.toString()}`);

            if (!response.ok) {
                throw new Error('Failed to load time-off requests');
            }

            const data: TimeOffRequestsResponse = await response.json();

            // Enrich with employee names
            const enrichedRequests = data.requests.map(req => {
                const employee = employees.find(e => e.id === req.employee_id);
                return {
                    ...req,
                    employee_first_name: employee?.first_name,
                    employee_last_name: employee?.last_name,
                };
            });

            setRequests(enrichedRequests);
        } catch (err) {
            console.error('Failed to load requests:', err);
            setError(err instanceof Error ? err.message : 'Failed to load requests');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, policyTypeFilter, employeeFilter, employees]);

    useEffect(() => {
        if (employees.length > 0) {
            loadRequests();
        }
    }, [loadRequests, employees]);

    // Open review modal
    const openReviewModal = (request: TimeOffRequest, action: 'approve' | 'deny') => {
        setReviewingRequest(request);
        setReviewAction(action);
        setManagerNote('');
    };

    // Close review modal
    const closeReviewModal = () => {
        setReviewingRequest(null);
        setReviewAction(null);
        setManagerNote('');
    };

    // Submit review
    const handleSubmitReview = async () => {
        if (!reviewingRequest || !reviewAction) return;

        try {
            setSubmitting(true);

            const payload = {
                status: reviewAction === 'approve' ? 'approved' : 'denied',
                manager_note: managerNote || null,
            };

            const response = await apiFetch(`/api/timesheet/time-off-requests/${reviewingRequest.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to update request');
            }

            closeReviewModal();
            await loadRequests();
        } catch (err) {
            console.error('Failed to submit review:', err);
            alert(err instanceof Error ? err.message : 'Failed to update request');
        } finally {
            setSubmitting(false);
        }
    };

    // Format date/time
    const formatDateTime = (timestamp: number, isAllDay: boolean): string => {
        const date = new Date(timestamp * 1000);
        const dateStr = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });

        if (isAllDay) {
            return `${dateStr} (Full day)`;
        }

        const timeStr = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
        return `${dateStr} ${timeStr}`;
    };

    // Get status badge variant
    const getStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
        switch (status) {
            case 'approved':
                return 'default';
            case 'pending':
                return 'secondary';
            case 'denied':
                return 'destructive';
            default:
                return 'outline';
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                            <CalendarIcon className="h-8 w-8" />
                            Time Off Requests
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Review and manage time-off requests from your team
                        </p>
                    </div>
                </div>

                {/* Error State */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {error}
                    </div>
                )}

                {/* Requests Table */}
                <div className="border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Employee</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Start</TableHead>
                                <TableHead>End</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Note</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                        Loading...
                                    </TableCell>
                                </TableRow>
                            ) : requests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                        No time-off requests found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                requests.map(request => (
                                    <TableRow key={request.id}>
                                        <TableCell className="font-medium">
                                            {request.employee_first_name} {request.employee_last_name}
                                        </TableCell>
                                        <TableCell>{request.policy_type_name || '—'}</TableCell>
                                        <TableCell>
                                            {formatDateTime(request.start_timestamp, request.is_all_day)}
                                        </TableCell>
                                        <TableCell>
                                            {formatDateTime(request.end_timestamp, request.is_all_day)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={getStatusBadgeVariant(request.status)}>
                                                {request.status.toUpperCase()}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="max-w-xs truncate">
                                            {request.employee_note || '—'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {request.status === 'pending' && (
                                                <div className="flex gap-2 justify-end">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => openReviewModal(request, 'approve')}
                                                    >
                                                        <CheckIcon className="h-4 w-4 mr-1" />
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => openReviewModal(request, 'deny')}
                                                    >
                                                        <XMarkIcon className="h-4 w-4 mr-1" />
                                                        Deny
                                                    </Button>
                                                </div>
                                            )}
                                            {request.status !== 'pending' && (
                                                <span className="text-sm text-muted-foreground">
                                                    {request.manager_note ? `Note: ${request.manager_note}` : '—'}
                                                </span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Review Modal */}
                <Dialog open={!!reviewingRequest} onOpenChange={(open) => !open && closeReviewModal()}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>
                                {reviewAction === 'approve' ? 'Approve' : 'Deny'} Time Off Request
                            </DialogTitle>
                        </DialogHeader>

                        {reviewingRequest && (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <div className="text-sm">
                                        <span className="font-medium">Employee:</span>{' '}
                                        {reviewingRequest.employee_first_name} {reviewingRequest.employee_last_name}
                                    </div>
                                    <div className="text-sm">
                                        <span className="font-medium">Type:</span>{' '}
                                        {reviewingRequest.policy_type_name}
                                    </div>
                                    <div className="text-sm">
                                        <span className="font-medium">Period:</span>{' '}
                                        {formatDateTime(reviewingRequest.start_timestamp, reviewingRequest.is_all_day)}
                                        {' → '}
                                        {formatDateTime(reviewingRequest.end_timestamp, reviewingRequest.is_all_day)}
                                    </div>
                                    {reviewingRequest.employee_note && (
                                        <div className="text-sm">
                                            <span className="font-medium">Employee Note:</span>{' '}
                                            {reviewingRequest.employee_note}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <Label htmlFor="manager-note">Manager Note (optional)</Label>
                                    <textarea
                                        id="manager-note"
                                        className="w-full border rounded px-3 py-2 mt-1"
                                        rows={3}
                                        value={managerNote}
                                        onChange={(e) => setManagerNote(e.target.value)}
                                        placeholder="Add a note about this decision..."
                                    />
                                </div>
                            </div>
                        )}

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={closeReviewModal}
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                onClick={handleSubmitReview}
                                disabled={submitting}
                                variant={reviewAction === 'approve' ? 'default' : 'destructive'}
                            >
                                {submitting ? 'Submitting...' : (reviewAction === 'approve' ? 'Approve' : 'Deny')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/people/timeoff/requests')({
    component: TimeOffRequestsPage,
})
